import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
	Loader2,
	PlayCircle,
	CheckCircle2,
	AlertTriangle,
	XCircle,
	ArrowRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import * as pipelineValidator from "@/services/pipelineValidator";
import type { PVBulkAuditResult, PVEmployeeAuditResult } from "@/types";
import { EmployeeDrillDown } from "./EmployeeDrillDown";

interface AuditDashboardProps {
	organizationId: string;
	periodStart: string;
	periodEnd: string;
}

const STAGES = [
	"config",
	"attendance",
	"leave",
	"timesheet",
	"payroll",
	"payslip",
	"weekend",
] as const;

const STAGE_LABELS: Record<string, string> = {
	config: "Config",
	attendance: "Attendance",
	leave: "Leave",
	timesheet: "Timesheet",
	payroll: "Payroll",
	payslip: "Payslip",
	weekend: "Weekend",
};

type StageStatus = "pass" | "warn" | "fail";

function getStageStatus(health: {
	pass: number;
	warn: number;
	fail: number;
}): StageStatus {
	if (health.fail > 0) return "fail";
	if (health.warn > 0) return "warn";
	return "pass";
}

const STATUS_ICON = {
	pass: <CheckCircle2 className="h-5 w-5 text-green-500" />,
	warn: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
	fail: <XCircle className="h-5 w-5 text-red-500" />,
};

const STATUS_DOT_COLOR = {
	pass: "bg-green-500",
	warn: "bg-yellow-500",
	fail: "bg-red-500",
};

const CARD_BORDER_COLOR = {
	pass: "border-green-200 dark:border-green-800",
	warn: "border-yellow-200 dark:border-yellow-800",
	fail: "border-red-200 dark:border-red-800",
};

function getEmployeeStageStatus(
	employee: PVEmployeeAuditResult,
	stage: string,
): StageStatus {
	const stageResult = employee.stages[stage as keyof typeof employee.stages];
	return stageResult?.status ?? "pass";
}

export function AuditDashboard({
	organizationId,
	periodStart,
	periodEnd,
}: AuditDashboardProps) {
	const { toast } = useToast();
	const [result, setResult] = useState<PVBulkAuditResult | null>(null);
	const [selectedEmployee, setSelectedEmployee] =
		useState<PVEmployeeAuditResult | null>(null);
	const [showFailuresOnly, setShowFailuresOnly] = useState(false);

	const auditMutation = useMutation({
		mutationFn: () =>
			pipelineValidator.runBulkAudit({
				organizationId,
				periodStart,
				periodEnd,
			}),
		onSuccess: (data) => {
			setResult(data);
			setSelectedEmployee(null);
			toast({
				title: "Audit complete",
				description: `Audited ${data.totalEmployees} employees`,
			});
		},
		onError: (err: Error) => {
			toast({
				title: "Audit failed",
				description: err.message,
				variant: "destructive",
			});
		},
	});

	if (selectedEmployee && result) {
		return (
			<EmployeeDrillDown
				employee={selectedEmployee}
				organizationId={organizationId}
				periodStart={periodStart}
				periodEnd={periodEnd}
				onBack={() => setSelectedEmployee(null)}
			/>
		);
	}

	const filteredEmployees = result?.employees.filter(
		(emp) => !showFailuresOnly || emp.overallStatus === "fail",
	);

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-3">
				<Button
					onClick={() => auditMutation.mutate()}
					disabled={
						!organizationId.trim() || auditMutation.isPending
					}
				>
					{auditMutation.isPending ? (
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
					) : (
						<PlayCircle className="mr-2 h-4 w-4" />
					)}
					Run Audit
				</Button>
			</div>

			{auditMutation.isPending && (
				<Card>
					<CardContent className="py-8 flex items-center justify-center gap-2 text-muted-foreground">
						<Loader2 className="h-5 w-5 animate-spin" />
						Running audit across all employees...
					</CardContent>
				</Card>
			)}

			{!result && !auditMutation.isPending && (
				<Card>
					<CardContent className="py-8 text-center text-muted-foreground">
						Run an audit to see validation results for all
						employees.
					</CardContent>
				</Card>
			)}

			{result && (
				<>
					{/* Stage health cards */}
					<div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
						{STAGES.map((stage) => {
							const health = result.stageHealth[stage] ?? {
								pass: 0,
								warn: 0,
								fail: 0,
							};
							const status = getStageStatus(health);
							const total =
								health.pass + health.warn + health.fail;

							return (
								<Card
									key={stage}
									className={`${CARD_BORDER_COLOR[status]}`}
								>
									<CardHeader className="pb-2 pt-3 px-3">
										<CardTitle className="text-sm font-medium flex items-center justify-between">
											{STAGE_LABELS[stage]}
											{STATUS_ICON[status]}
										</CardTitle>
									</CardHeader>
									<CardContent className="px-3 pb-3">
										<p className="text-lg font-semibold font-mono">
											{health.pass}/{total}{" "}
											<span className="text-xs font-normal text-muted-foreground">
												pass
											</span>
										</p>
									</CardContent>
								</Card>
							);
						})}
					</div>

					{/* Employee results table */}
					<Card>
						<CardHeader className="pb-3">
							<div className="flex items-center justify-between">
								<CardTitle className="text-base">
									Employee Results ({result.totalEmployees})
								</CardTitle>
								<label className="flex items-center gap-2 text-sm cursor-pointer">
									<input
										type="checkbox"
										checked={showFailuresOnly}
										onChange={(e) =>
											setShowFailuresOnly(
												e.target.checked,
											)
										}
										className="rounded border-input"
									/>
									Show Failures Only
								</label>
							</div>
						</CardHeader>
						<CardContent>
							{filteredEmployees &&
							filteredEmployees.length > 0 ? (
								<div className="overflow-x-auto">
									<table className="w-full text-sm">
										<thead>
											<tr className="border-b text-left">
												<th className="pb-2 pr-4 font-medium">
													Employee
												</th>
												<th className="pb-2 pr-4 font-medium">
													Employment Type
												</th>
												{STAGES.map((stage) => (
													<th
														key={stage}
														className="pb-2 px-2 font-medium text-center"
														title={
															STAGE_LABELS[stage]
														}
													>
														{STAGE_LABELS[
															stage
														].slice(0, 3)}
													</th>
												))}
												<th className="pb-2 pl-4 font-medium">
													Action
												</th>
											</tr>
										</thead>
										<tbody>
											{filteredEmployees.map((emp) => (
												<tr
													key={emp.userId}
													className="border-b last:border-0 hover:bg-muted/50"
												>
													<td className="py-2 pr-4">
														<div className="font-medium">
															{emp.employeeName}
														</div>
														<div className="text-xs text-muted-foreground">
															{emp.salaryBasis}
														</div>
													</td>
													<td className="py-2 pr-4">
														<Badge variant="outline">
															{emp.employmentType}
														</Badge>
													</td>
													{STAGES.map((stage) => {
														const stageStatus =
															getEmployeeStageStatus(
																emp,
																stage,
															);
														return (
															<td
																key={stage}
																className="py-2 px-2 text-center"
															>
																<span
																	className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_DOT_COLOR[stageStatus]}`}
																	title={`${STAGE_LABELS[stage]}: ${stageStatus}`}
																/>
															</td>
														);
													})}
													<td className="py-2 pl-4">
														<Button
															variant="ghost"
															size="sm"
															onClick={() =>
																setSelectedEmployee(
																	emp,
																)
															}
														>
															Drill Down
															<ArrowRight className="ml-1 h-3 w-3" />
														</Button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : (
								<p className="text-center text-muted-foreground py-4">
									{showFailuresOnly
										? "No failures found."
										: "No employee results."}
								</p>
							)}
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}
